import { useEffect, useRef, useState } from 'react'
import Cropper from 'cropperjs'
import type { CropperSelection } from 'cropperjs'
import { useT } from '../i18n'

// Crop-on-upload, square 1:1 (ADR 0052). The source is a local File the user just picked, so we crop a
// blob: URL in the browser and hand back the cropped square blob — the avatar file IS the crop. No
// stored crop-rect, no derivative pipeline.
export function AvatarCropModal({ file, onSave, onCancel }: { file: File; onSave: (blob: Blob) => void; onCancel: () => void }) {
  const t = useT()
  const imageRef = useRef<HTMLImageElement>(null)
  const cropperRef = useRef<Cropper | null>(null)
  const [objectUrl] = useState(() => URL.createObjectURL(file))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!imageRef.current) return
    cropperRef.current = new Cropper(imageRef.current, {
      container: imageRef.current.parentElement ?? undefined,
      template: `
        <cropper-canvas background>
          <cropper-image rotatable scalable translatable></cropper-image>
          <cropper-shade hidden></cropper-shade>
          <cropper-selection initial-coverage="0.85" aspect-ratio="1" precise outlined movable resizable>
            <cropper-grid role="grid" bordered covered></cropper-grid>
            <cropper-crosshair centered></cropper-crosshair>
            <cropper-handle action="move"></cropper-handle>
            <cropper-handle action="n"></cropper-handle>
            <cropper-handle action="e"></cropper-handle>
            <cropper-handle action="s"></cropper-handle>
            <cropper-handle action="w"></cropper-handle>
            <cropper-handle action="ne"></cropper-handle>
            <cropper-handle action="nw"></cropper-handle>
            <cropper-handle action="se"></cropper-handle>
            <cropper-handle action="sw"></cropper-handle>
          </cropper-selection>
          <cropper-handle action="move" plain></cropper-handle>
        </cropper-canvas>
      `,
      zoomOnTouch: true,
      zoomOnWheel: true,
    } as any)
    return () => {
      cropperRef.current?.destroy()
      cropperRef.current = null
    }
  }, [objectUrl])

  useEffect(() => () => URL.revokeObjectURL(objectUrl), [objectUrl])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const selection = cropperRef.current?.getCropperSelection() as CropperSelection | null
      if (!selection) return
      // Cap the exported square so avatars stay small.
      const canvas = await (selection as any).$toCanvas({ width: 512, height: 512 })
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b: Blob | null) => (b ? resolve(b) : reject(new Error('export failed'))), 'image/webp', 0.9)
      })
      onSave(blob)
    } catch {
      setError(t.cropExportError)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md w-full">
        <h3 className="font-bold text-lg mb-4">{t.adjustPhoto}</h3>
        <div className="relative w-full rounded-xl bg-base-200" style={{ height: '360px', overflow: 'hidden' }}>
          <img ref={imageRef} src={objectUrl} alt={t.adjustPhoto} className="block w-full h-full" style={{ objectFit: 'contain' }} />
        </div>
        <p className="text-sm text-base-content/60 mt-3">{t.cropHint}</p>
        {error && <div className="alert alert-error mt-3"><span>{error}</span></div>}
        <div className="modal-action">
          <button onClick={onCancel} className="btn btn-ghost" disabled={saving}>{t.cancel}</button>
          <button onClick={() => void handleSave()} className="btn btn-primary" disabled={saving}>
            {saving ? <span className="loading loading-spinner loading-sm" /> : null}
            {t.savePhoto}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onCancel} />
    </div>
  )
}
